import { REGEX_PATTERNS } from '@core/constants/regex.constants'
import { getCurrentAcademicYear } from '@app/core/constants/valueConstants'

/**
 * Start calendar year of the current academic cycle (AY label).
 * Delegates to `getCurrentAcademicYear` in `valueConstants` (rollover 1 July local).
 */
export function getCurrentAcademicYearStartYear(now: Date = new Date()): number {
    return getCurrentAcademicYear(now)
}

/**
 * Formats an academic year start calendar year as "YYYY-YYYY" (e.g. 2025 → "2025-2026").
 */
export function formatAcademicYearLabel(startYear: number): string {
    return `${startYear}-${startYear + 1}`
}

/**
 * Default academic year for new leads and filters, in "YYYY-YYYY" format (e.g. "2026-2027").
 * Uses the same July-boundary rule as lead and revenue APIs.
 */
export function getDefaultAcademicYear(): string {
    return formatAcademicYearLabel(getCurrentAcademicYearStartYear())
}

/**
 * Generates academic year options array in "YYYY-YYYY" format
 * @param startYear - Starting academic year (defaults to current cycle per {@link getCurrentAcademicYearStartYear})
 * @param count - Number of years to generate (defaults to 3: current + next two)
 * @returns Array of academic year strings in "YYYY-YYYY" format
 */
export function generateAcademicYearOptions(startYear?: number, count = 3): string[] {
    const baseYear = startYear ?? getCurrentAcademicYearStartYear()
    return Array.from({ length: count }, (_, index) => formatAcademicYearLabel(baseYear + index))
}

/**
 * Extracts the starting year from academic year string
 * @param academicYear - Academic year in "YYYY-YYYY" format (e.g., "2025-2026")
 * @returns The starting year as a number (e.g., 2025)
 */
/**
 * Parses an academic year label to its start calendar year.
 * Falls back to the current academic cycle when empty or invalid.
 */
export function parseAcademicYearStartYearFromLabel(
    value: string | null | undefined,
    now: Date = new Date(),
): number {
    if (!value) {
        return getCurrentAcademicYearStartYear(now)
    }
    const labelMatch = String(value).match(REGEX_PATTERNS.ACADEMIC_YEAR_LABEL_ANCHORED)
    if (labelMatch) {
        return Number(labelMatch[1])
    }
    return getCurrentAcademicYearStartYear(now)
}

export function extractYearFromAcademicYear(academicYear: string): number | null {
    if (!academicYear) return null

    // Handle format like "2024-2025" - extract the first year
    // This regex matches the first 4 digits at the start of the string
    const yearMatch = academicYear.match(REGEX_PATTERNS.ACADEMIC_YEAR_EXTRACT)
    if (yearMatch) {
        return parseInt(yearMatch[1], 10)
    }

    // Try parsing as number directly (fallback)
    const year = parseInt(academicYear, 10)
    return isNaN(year) ? null : year
}

/**
 * Validates that an academic year falls within the allowed future window from the current AY.
 * Default policy is current AY + next two start years.
 */
export function isAcademicYearWithinAllowedFutureRange(
    academicYear: string,
    maxFutureYears = 2,
    now: Date = new Date(),
): boolean {
    const selectedYear = extractYearFromAcademicYear(academicYear)
    if (!selectedYear) {
        return false
    }
    const baseYear = getCurrentAcademicYearStartYear(now)
    return selectedYear >= baseYear && selectedYear <= baseYear + maxFutureYears
}
